import {
  getHierarchy,
  getHierarchyWithFilter,
  getHierarchyWithFilterDebounced
} from '@/services/people-api.service'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, unref, watch } from 'vue'
import { stratify } from 'd3-hierarchy'
import { isDefault } from '@/hooks/use-filters'
import { useHierarchyOptions, useHierarchyState } from './use-hierarchy'
import { isEmpty } from 'lodash-es'
import { useStore } from 'vuex'

/**
 * Calculate direct and total subordinate counts for a given node.
 *
 * @param {Object} node - The node to calculate the subordinate counts for.
 * @returns {Object} An object with the direct and total subordinate counts..
 */
export function calculateSubordinateCounts(node) {
  const children = node.children || []
  let directSubordinates = 0
  let totalSubordinates = 0

  for (const child of children) {
    const childTotalSubordinates = child.data?.totalSubordinates || 0

    if (child.data?.skip) {
      totalSubordinates += childTotalSubordinates
      continue
    }

    if (child.data?.collapsed) {
      directSubordinates += child.data?.directSubordinates || 0
      totalSubordinates += childTotalSubordinates
      continue
    }

    directSubordinates += 1
    totalSubordinates += 1 + childTotalSubordinates
  }

  return {
    directSubordinates,
    totalSubordinates
  }
}

export function buildHierarchy(data) {
  //ignore all employee-data nodes in the hierarchy
  const filteredData = data.filter(
    (node) => node.id !== 'p_employee_data' && node.parentId !== 'p_employee_data'
  )

  const result = stratify()
    .id((node) => node.id)
    .parentId((node) => node.parentId)(filteredData)

  result.eachAfter((node) => {
    const { directSubordinates, totalSubordinates } = calculateSubordinateCounts(node)

    node.data.directSubordinates = directSubordinates
    node.data.totalSubordinates = totalSubordinates
  })

  return result
}

async function fetchHierarchyData({
  boardId,
  options = {},
  signal = null,
  forceFresh = false,
  useDebouncedFilter = true
}) {
  const filters = options?.filters || null
  const includeApprovedRoles = options?.includeApprovedRoles || false
  const collapseOn = isEmpty(options?.collapseOn) ? null : options?.collapseOn

  if (!filters || isDefault(filters)) {
    return getHierarchy({
      boardId,
      includeApprovedRoles,
      collapseOn,
      forceFresh
    })
  }

  const fetchFilteredHierarchy = useDebouncedFilter
    ? getHierarchyWithFilterDebounced
    : getHierarchyWithFilter

  return fetchFilteredHierarchy({
    boardId,
    filters,
    includeApprovedRoles,
    collapseOn,
    signal
  })
}

export function useHierarchyFetch(boardId, view = null) {
  if (!unref(boardId)) return {}
  const { getters } = useStore()

  const uid = computed(() => getters.uid)

  const viewKey = computed(() => {
    if (!unref(view)) {
      return unref(boardId)
    }

    return `${unref(boardId)}-${unref(view)}`
  })

  const { options } = useHierarchyOptions(boardId, view)
  const { setHierarchy, setHierarchyData, setIsLoading, setIsRefreshing } = useHierarchyState(
    boardId,
    view
  )

  const optionsForView = computed(() => options.value[viewKey.value] || {})

  const queryKey = computed(() => {
    const queryOptions = {
      uid: unref(uid),
      boardId: unref(boardId),
      view: unref(view),
      filters: optionsForView.value?.filters || null,
      includeApprovedRoles: optionsForView.value?.includeApprovedRoles || false,
      collapseOn: isEmpty(optionsForView.value?.collapseOn)
        ? null
        : optionsForView.value?.collapseOn
    }

    if (queryOptions.filters === null || isDefault(queryOptions.filters)) {
      delete queryOptions.filters
    }

    return ['hierarchy', { ...queryOptions }]
  })

  /**
   * Fetches the hierarchy data from the API. Simple array of { id, parentId }
   * INFO:
   * Frontend cache for hierarchy is now disabled by setting cacheTime to 0.
   * This means the data will not be stored in the cache and a new request will be made every time.
   *
   * Further reading: https://tanstack.com/query/latest/docs/framework/vue/guides/caching
   */
  const { isPending, isRefetching } = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      fetchHierarchyData({
        boardId: unref(boardId),
        options: optionsForView.value,
        signal
      }),
    select: (data) => {
      setHierarchy(buildHierarchy(data))
      setHierarchyData(data)

      // Return a lightweight value to keep TanStack Query cache slim.
      // The full hierarchy payload is stored via setHierarchyData instead.
      return Array.isArray(data) ? data.length : 0
    },
    enabled: () => !isEmpty(unref(boardId)),
    structuralSharing: (oldData, newData) => {
      if (typeof oldData === 'number' && typeof newData === 'number') {
        return oldData === newData ? oldData : newData
      }

      if (Array.isArray(oldData) && Array.isArray(newData) && oldData.length === newData.length) {
        for (let i = 0; i < oldData.length; i++) {
          if (dataChanged(oldData[i], newData[i])) {
            return newData
          }
        }
        return oldData
      }

      return newData
    },
    cacheTime: 0
    // staleTime: 5 * 1000 * 60
  })

  function dataChanged(oldNode, newNode) {
    if (
      oldNode.id !== newNode.id ||
      oldNode.parentId !== newNode.parentId ||
      oldNode.data?.skip !== newNode.data?.skip ||
      oldNode.data?.collapsed !== newNode.data?.collapsed
    ) {
      return newNode
    }
    return oldNode
  }

  watch(
    isPending,
    () => {
      setIsLoading(isPending.value)
    },
    { immediate: true }
  )

  watch(
    isRefetching,
    () => {
      setIsRefreshing(isRefetching.value)
    },
    { immediate: true }
  )
}

export function useHierarchyHelper(boardId, view = null) {
  const queryClient = useQueryClient()
  const { getters } = useStore()
  const uid = computed(() => getters.uid)
  const viewKey = computed(() => {
    if (!unref(view)) {
      return unref(boardId)
    }

    return `${unref(boardId)}-${unref(view)}`
  })
  const { options } = useHierarchyOptions(boardId, view)
  const optionsForView = computed(() => options.value[viewKey.value] || {})
  const { setHierarchy, setHierarchyData, setIsRefreshing } = useHierarchyState(boardId, view)
  const queryKey = computed(() => {
    const queryOptions = {
      uid: unref(uid),
      boardId: unref(boardId),
      view: unref(view),
      filters: optionsForView.value?.filters || null,
      includeApprovedRoles: optionsForView.value?.includeApprovedRoles || false,
      collapseOn: isEmpty(optionsForView.value?.collapseOn)
        ? null
        : optionsForView.value?.collapseOn
    }

    if (queryOptions.filters === null || isDefault(queryOptions.filters)) {
      delete queryOptions.filters
    }

    return ['hierarchy', { ...queryOptions }]
  })

  async function invalidate() {
    await queryClient.invalidateQueries({ queryKey: ['hierarchy', { boardId: unref(boardId) }] })
  }

  async function forceInvalidate() {
    const resolvedBoardId = unref(boardId)

    if (!resolvedBoardId) return

    setIsRefreshing(true)

    try {
      const data = await fetchHierarchyData({
        boardId: resolvedBoardId,
        options: optionsForView.value,
        forceFresh: true,
        useDebouncedFilter: false
      })

      setHierarchy(buildHierarchy(data))
      setHierarchyData(data)
      queryClient.setQueryData(queryKey.value, Array.isArray(data) ? data.length : 0)
    } finally {
      setIsRefreshing(false)
    }
  }

  return {
    invalidate,
    forceInvalidate
  }
}
